import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Row, Col, Card, CardBody, Badge, Button,
  Breadcrumb, BreadcrumbItem
} from 'reactstrap';
import {
  ArrowLeft, Calendar, Clock, User, Eye, Edit,
  Tag, Folder, Link as LinkIcon
} from 'react-feather';
import axios from 'axios';
import moment from 'moment';
import Swal from 'sweetalert2';
import { baseURL } from '../../../../Services/api/baseURL';
import Loader from '../../../Loader/Loader';
import { H1, H4 } from '../../../../AbstractElements';
import BlogComments from './BlogComments';
import useRbac from '../../../../Hooks/useRbac';

const BlogDetail = () => {
  const { id, slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = JSON.parse(localStorage.getItem("token"));
  const userRole = JSON.parse(localStorage.getItem('role_name'));
  const { can } = useRbac();
  const canEditBlog = can('blogs.update');

  useEffect(() => {
    fetchBlogDetail();
  }, [id, slug]);

  const fetchBlogDetail = async () => {
    try {
      setLoading(true);
      let response;
      
      // Fetch by slug if available, otherwise by ID
      if (slug) {
        response = await axios.get(`${baseURL}/api/blogs/slug/${slug}`, {
          headers: token ? { Authorization: `${token}` } : {}
        });
      } else {
        response = await axios.get(`${baseURL}/api/blogs/${id}`, {
          headers: token ? { Authorization: `${token}` } : {}
        });
      }

      const blogData = response.data.data;
      setBlog(blogData);
      
      // Fetch related blogs
      if (blogData?._id) {
        fetchRelatedBlogs(blogData._id);
      }
    } catch (error) {
      console.error('Error fetching blog:', error);
      Swal.fire({
        title: "Error",
        text: "Failed to load blog details",
        icon: "error",
        confirmButtonColor: "#fc2c54",
      });
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedBlogs = async (blogId) => {
    try {
      const response = await axios.get(`${baseURL}/api/blogs/related/${blogId}`, {
        headers: token ? { Authorization: `${token}` } : {}
      });
      setRelatedBlogs(response.data.data || []);
    } catch (error) {
      console.error('Error fetching related blogs:', error);
    }
  };

  // Get image URL and alt text
  const getImageData = (image) => {
    if (typeof image === 'object' && image !== null) {
      return { url: image.url, altText: image.altText || blog?.title || 'Blog image' };
    }
    return { url: image, altText: blog?.title || 'Blog image' };
  };

  const handleEditBlog = () => {
    navigate(`/blog-edit/${blog._id}`);
  };

  const handleBackToBlog = () => {
    navigate('/blog');
  };

  const copyBlogUrl = () => {
    const url = `${window.location.origin}/blogs/${blog.slug}`;
    navigator.clipboard.writeText(url);
    Swal.fire({
      title: "Link Copied!",
      text: "Blog URL has been copied to clipboard",
      icon: "success",
      timer: 2000,
      showConfirmButton: false
    });
  };

  if (loading) {
    return (
      <Container fluid style={{ paddingTop: '30px', minHeight: '60vh' }}>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Loader />
        </div>
      </Container>
    );
  }

  if (!blog) {
    return (
      <Container fluid style={{ paddingTop: '30px' }}>
        <div className="text-center">
          <h4>Blog not found</h4>
          <Button color="bt btn-primary" onClick={handleBackToBlog}>
            <ArrowLeft size={16} className="me-2" />
            Back to Blog List
          </Button>
        </div>
      </Container>
    );
  }

  // Get primary image
  const primaryImage = blog.images && blog.images.length > 0 
    ? getImageData(blog.images[0]) 
    : null;

  return (
    <Container fluid style={{ paddingTop: '30px' }}>
      {/* Breadcrumb */}
      <Row className="mb-4">
        <Col xs={12}>
          <Breadcrumb>
            <BreadcrumbItem>
              <Button
                color="link"
                className="p-0 text-decoration-none"
                onClick={handleBackToBlog}
              >
                Blog Management
              </Button>
            </BreadcrumbItem>
            <BreadcrumbItem active>{blog.title}</BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>

      {/* Header Actions */}
      <Row className="mb-4">
        <Col xs={12} className="d-flex justify-content-between align-items-center">
          <Button className="cancel_Btn" onClick={handleBackToBlog} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <ArrowLeft size={16} />
            Back to List
          </Button>

          <div className="d-flex gap-2">
            <Button 
              color="outline-secondary" 
              onClick={copyBlogUrl} 
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <LinkIcon size={16} />
              Copy URL
            </Button>
            {canEditBlog && (
              <Button color="secondary" onClick={handleEditBlog} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Edit size={16} className="me-2" />
                Edit Blog
              </Button>
            )}
          </div>
        </Col>
      </Row>

      <Row>
        {/* Main Content */}
        <Col lg={8}>
          <Card className="shadow-sm">
            <CardBody className="p-4">
              {/* Blog Header */}
              <div className="mb-4">
                {/* Status Badge */}
                <div className="mb-3">
                  <Badge
                    color={blog.status === 'active' ? 'success' : blog.status === 'draft' ? 'warning' : 'danger'}
                    className="fs-6 px-3 py-2"
                  >
                    {blog.status === 'active' ? 'Published' : blog.status === 'draft' ? 'Draft' : 'Unpublished'}
                  </Badge>
                </div>

                {/* Category and Tags */}
                <div className="mb-3">
                  {(blog.category || blog.categoryName) && (
                    <Badge color="info" className="me-2">
                      <Folder size={12} className="me-1" />
                      {blog.category?.name || blog.categoryName}
                    </Badge>
                  )}
                  {blog.tags && blog.tags.map((tag, index) => (
                    <Badge key={index} color="outline-secondary" className="me-1 mb-1">
                      <Tag size={10} className="me-1" />
                      {tag}
                    </Badge>
                  ))}
                </div>

                {/* Title */}
                <H1 className="mb-4 fw-bold" style={{ lineHeight: '1.3', color: '#2c3e50' }}>
                  {blog.title}
                </H1>

                {/* SEO-Friendly URL Display */}
                {blog.slug && (
                  <div className="mb-3 p-2" style={{ backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                    <small className="text-muted d-flex align-items-center gap-1">
                      <LinkIcon size={12} />
                      URL: <code>/blogs/{blog.slug}</code>
                    </small>
                  </div>
                )}

                {/* Meta Info */}
                <div className="d-flex flex-wrap align-items-center text-muted mb-4">
                  <div className="me-4 mb-2">
                    <User size={16} className="me-2" />
                    <span className="fw-medium">{blog.createdBy?.name || 'Admin'}</span>
                  </div>
                  <div className="me-4 mb-2">
                    <Calendar size={16} className="me-2" />
                    <span>{moment(blog.publishDate || blog.createdTime || blog.createdAt).format("MMMM DD, YYYY")}</span>
                  </div>
                  <div className="me-4 mb-2">
                    <Clock size={16} className="me-2" />
                    <span>
                      {moment(blog.publishDate || blog.createdTime || blog.createdAt).format("hh:mm A")}
                    </span>
                  </div>
                  {blog.readTime && (
                    <div className="me-4 mb-2">
                      <Eye size={16} className="me-2" />
                      <span>{blog.readTime}</span>
                    </div>
                  )}
                  {blog.viewCount > 0 && (
                    <div className="mb-2">
                      <Eye size={16} className="me-2" />
                      <span>{blog.viewCount} views</span>
                    </div>
                  )}
                </div>

                {/* Excerpt */}
                {blog.excerpt && (
                  <div className="bg-light p-3 rounded mb-4">
                    <p className="mb-0 fst-italic fs-5 text-secondary">
                      {blog.excerpt}
                    </p>
                  </div>
                )}
              </div>

              {/* Featured Image with Alt Text */}
              {primaryImage && (
                <div className="mb-4">
                  <img
                    src={primaryImage.url}
                    alt={primaryImage.altText}
                    className="img-fluid rounded shadow-sm"
                    style={{ width: '100%', maxHeight: '400px', objectFit: 'cover' }}
                  />
                  {primaryImage.altText && canEditBlog && (
                    <small className="text-muted d-block mt-1">
                      Alt text: {primaryImage.altText}
                    </small>
                  )}
                </div>
              )}

              {/* Blog Content */}
              <div
                className="blog-content"
                dangerouslySetInnerHTML={{
                  __html: blog.content || blog.description
                }}
                style={{
                  fontSize: '16px',
                  lineHeight: '1.8',
                  color: '#374151'
                }}
              />

              {/* SEO Meta Information (for admin) */}
              {canEditBlog && (blog.metaTitle || blog.metaDescription) && (
                <div className="mt-5 pt-4 border-top">
                  <H4 className="mb-3">SEO Information</H4>
                  {blog.metaTitle && (
                    <div className="mb-3">
                      <strong>Meta Title:</strong>
                      <p className="text-muted mb-0">{blog.metaTitle}</p>
                    </div>
                  )}
                  {blog.metaDescription && (
                    <div className="mb-3">
                      <strong>Meta Description:</strong>
                      <p className="text-muted mb-0">{blog.metaDescription}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Timestamps */}
              <div className="mt-5 pt-4 border-top">
                <Row className="text-muted small">
                  <Col md={6}>
                    <strong>Created:</strong> {moment(blog.createdTime || blog.createdAt).format("MMMM DD, YYYY at hh:mm A")}
                  </Col>
                  {blog.updatedAt && blog.updatedAt !== blog.createdAt && (
                    <Col md={6}>
                      <strong>Last Updated:</strong> {moment(blog.updatedAt).format("MMMM DD, YYYY at hh:mm A")}
                    </Col>
                  )}
                </Row>
              </div>
            </CardBody>
          </Card>

          {/* Comments Section */}
          <BlogComments 
            blogId={blog._id} 
            commentsEnabled={blog.commentsEnabled !== false}
            isAdmin={canEditBlog}
          />
        </Col>

        {/* Sidebar */}
        <Col lg={4}>
          {/* Blog Statistics */}
          <Card className="shadow-sm mb-4">
            <CardBody>
              <H4 className="mb-3">Blog Statistics</H4>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span>Status:</span>
                <Badge color={blog.status === 'active' ? 'success' : blog.status === 'draft' ? 'warning' : 'danger'}>
                  {blog.status === 'active' ? 'Published' : blog.status === 'draft' ? 'Draft' : 'Unpublished'}
                </Badge>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span>Word Count:</span>
                <span className="fw-bold">
                  {blog.content ? blog.content.replace(/<[^>]*>/g, '').split(/\s+/).length : 0} words
                </span>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span>Reading Time:</span>
                <span className="fw-bold">{blog.readTime || 'Not calculated'}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span>Views:</span>
                <span className="fw-bold">{blog.viewCount || 0}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span>Comments:</span>
                <span className="fw-bold">{blog.commentCount || 0}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <span>Images:</span>
                <span className="fw-bold">{blog.images ? blog.images.length : 0}</span>
              </div>
            </CardBody>
          </Card>

          {/* Related Blogs */}
          {relatedBlogs && relatedBlogs.length > 0 && (
            <Card className="shadow-sm">
              <CardBody>
                <H4 className="mb-3">Related Articles</H4>
                {relatedBlogs.map((relatedBlog) => {
                  const relatedImage = relatedBlog.images && relatedBlog.images.length > 0
                    ? getImageData(relatedBlog.images[0])
                    : null;
                    
                  return (
                    <div key={relatedBlog._id} className="mb-3 pb-3 border-bottom">
                      <div className="d-flex">
                        {relatedImage && (
                          <img
                            src={relatedImage.url}
                            alt={relatedImage.altText}
                            className="rounded me-3"
                            style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                          />
                        )}
                        <div>
                          <h6
                            className="mb-1 cursor-pointer text-primary"
                            onClick={() => navigate(`/blog-detail/${relatedBlog._id}`)}
                            style={{ cursor: 'pointer', fontSize: '14px' }}
                          >
                            {relatedBlog.title}
                          </h6>
                          <small className="text-muted">
                            {moment(relatedBlog.createdTime || relatedBlog.createdAt).format("MMM DD, YYYY")}
                          </small>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardBody>
            </Card>
          )}
        </Col>
      </Row>

      <style jsx>{`
        .blog-content {
          font-family: 'Georgia', serif;
        }
        .blog-content h1, .blog-content h2, .blog-content h3, 
        .blog-content h4, .blog-content h5, .blog-content h6 {
          margin-top: 2rem;
          margin-bottom: 1rem;
          font-weight: 600;
        }
        .blog-content p {
          margin-bottom: 1.2rem;
        }
        .blog-content blockquote {
          border-left: 4px solid #007bff;
          padding-left: 1rem;
          margin: 1.5rem 0;
          font-style: italic;
          background-color: #f8f9fa;
          padding: 1rem;
          border-radius: 0.25rem;
        }
        .blog-content img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          margin: 1rem 0;
        }
        .blog-content ul, .blog-content ol {
          margin-bottom: 1.2rem;
          padding-left: 2rem;
        }
        .blog-content li {
          margin-bottom: 0.5rem;
        }
        .blog-content code {
          background-color: #f1f3f4;
          padding: 0.2rem 0.4rem;
          border-radius: 0.25rem;
          font-size: 0.875rem;
        }
        .blog-content pre {
          background-color: #f8f9fa;
          padding: 1rem;
          border-radius: 0.5rem;
          overflow-x: auto;
          margin: 1rem 0;
        }
        .blog-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 1rem 0;
        }
        .blog-content table th,
        .blog-content table td {
          border: 1px solid #dee2e6;
          padding: 0.75rem;
        }
        .blog-content table th {
          background-color: #f8f9fa;
        }
        .cursor-pointer {
          cursor: pointer;
        }
        .cursor-pointer:hover {
          text-decoration: underline;
        }
      `}</style>
    </Container>
  );
};

export default BlogDetail;